/* tmc-home.js — the single motion script for the tmc-* home sections (spec §3).
   One one-shot IntersectionObserver (one per data-reveal-threshold, default .18) adds .is-in; children stagger via --i; S2 parallax; S7 outline draw;
   the hero's optional video rotation and its tracked labels (NW-22, NW-22b), and its 390 area strip (NW-61); the home quiz phone's loop (NW-23).
   Guarded so it runs once however many sections include snippets/tmc-home-assets.liquid. */
(function () {
  if (window.__tmcHome) return;
  window.__tmcHome = true;

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var HOME_THRESHOLD = 0.18;

  function reveal(root) {
    var els = (root || document).querySelectorAll('[data-reveal]:not(.is-in),[data-draw]:not(.is-in)');
    if (!els.length) return;
    if (reduce.matches || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    /* One observer per threshold. Home sections use HOME_THRESHOLD and keep their original rule: any
       overlap a callback reports reveals, with an -8% bottom margin. A band that sets
       data-reveal-threshold gets an exact rule instead (the PDP steps motion board plays at 30% in view,
       NW-46): no margin, and the first callback's sliver at load does not start it. Two guards keep that
       band from staying hidden: a 1% tolerance for a ratio reported a hair under the threshold, and
       extra callback points so a band too tall to ever show 30% still reveals once it fills 90% of the
       viewport. */
    var observers = {};
    function observer(threshold) {
      if (observers[threshold]) return observers[threshold];
      var exact = threshold !== HOME_THRESHOLD;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          if (exact && en.intersectionRatio < threshold - 0.01 &&
              !(en.rootBounds && en.intersectionRect.height >= en.rootBounds.height * 0.9)) return;
          en.target.classList.add('is-in');
          io.unobserve(en.target);
        });
      }, exact
        ? { threshold: [0.1, 0.2, threshold, 0.4, 0.6, 0.8], rootMargin: '0px' }
        : { threshold: HOME_THRESHOLD, rootMargin: '0px 0px -8% 0px' });
      observers[threshold] = io;
      return io;
    }

    els.forEach(function (el) {
      el.querySelectorAll('[data-reveal-item]').forEach(function (child, i) {
        child.style.setProperty('--i', i);
      });
      if (el.hasAttribute('data-draw')) {
        el.querySelectorAll('path').forEach(function (p) {
          if (p.getTotalLength) p.style.setProperty('--len', Math.ceil(p.getTotalLength()));
        });
      }
      var t = parseFloat(el.getAttribute('data-reveal-threshold'));
      observer(t > 0 && t <= 1 ? t : HOME_THRESHOLD).observe(el);
    });
  }

  var parallaxBound = false;
  function parallax() {
    var els = document.querySelectorAll('[data-parallax]');
    if (!els.length || reduce.matches) return;
    var ticking = false;
    function frame() {
      var vh = window.innerHeight;
      els.forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh) return;
        var progress = Math.min(1, Math.max(0, (vh - r.top) / (vh + r.height)));
        var distance = parseFloat(el.getAttribute('data-parallax')) || 60;
        el.style.transform = 'translateY(' + (-distance * (1 - progress)).toFixed(1) + 'px)';
      });
      ticking = false;
    }
    function onScroll() { if (!ticking) { ticking = true; window.requestAnimationFrame(frame); } }
    if (!parallaxBound) {
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      parallaxBound = true;
    }
    onScroll();
  }

  /* Hero media (NW-22, board motion-hero-media-mobile-drawer; NW-22b, the four-clip rotation with tracked labels).
     The poster paints first and stays underneath, with clip 01's labels placed at frame 0 as soon as the DOM is
     ready. After window load the current width's clips are taken out of their <template>s one at a time (the
     Shopify preview image dropped; clip 01 gets the poster), and the next clip loads while one plays. Each clip
     hands over HANDOFF seconds before it ends with a 900ms crossfade (the slide's CSS). Reduced motion or saveData:
     no video is created and nothing rotates; the labels stay at frame 0. A clip that errors, or has no canplay in
     6s, leaves the rotation. Everything pauses below 10% in view, in a hidden tab, or when the visitor presses
     pause, and only the first two resume on their own.
     Labels: each is a point per frame, 0-1 of the whole frame (data-hero-tracks), mapped through the video's own
     object-fit: cover crop and object-position (through strip() when the phone cut is portrait) on every presented frame (requestVideoFrameCallback, rAF where it
     is missing) and on every resize. A label outside the hero's edge, over the nav, the copy, the rail or the pause
     control, or over a label already placed, is hidden. */
  var saveData = !!(navigator.connection && navigator.connection.saveData);
  /* NW-22c: a connection the browser rates 3G or worse gets the poster, as Save-Data does. Only Chromium browsers rate
     it (Network Information API's effectiveType); Safari says nothing. `downlink` is not used: it is Chrome's rough
     estimate from recent traffic and reads low early in a visit (headless Chrome on a fast line reports 1.7 Mbps), so a
     cut-off on it would take the video from people who can play it. A slow "4G" line, or any slow iPhone, is caught by
     the stall guard below, which watches what actually happens. */
  function slowNetwork() {
    var c = navigator.connection;
    return !!(c && c.effectiveType && c.effectiveType !== '4g');
  }
  /* A clip that is on screen and keeps stopping to buffer means the connection cannot keep up: after one stall longer
     than STALL_MS, or STALLS stalls of BLIP_MS or more, the hero goes back to the poster for the rest of the visit. */
  var STALL_MS = 2000;
  var STALLS = 3;
  var BLIP_MS = 500;
  var phone = window.matchMedia('(max-width: 767px)');
  var TIMEOUT = 6000;
  var HANDOFF = 0.9;
  var EDGE = 12;   /* px a label keeps from the hero's edge */
  var CLEAR = 8;   /* px a label keeps from the nav, the copy, the rail and the pause control */
  var GAP = 6;     /* px between two labels; the later one yields while their people cross */

  /* Where object-fit: cover puts an iw x ih frame in a cw x ch box, with object-position fx, fy (0-1). */
  function cover(iw, ih, cw, ch, fx, fy) {
    var k = Math.max(cw / iw, ch / ih), w = iw * k, h = ih * k;
    return { x: (cw - w) * fx, y: (ch - h) * fy, w: w, h: h, cw: cw, ch: ch };
  }
  /* A portrait phone cut (1080x1920, from the 4K master) is not the whole clip: it is the full-height strip of the wide
     frame the phone showed before, `a` (its own width / height, 9/16) of the frame's height wide, taken at the phone
     focal point fx. The tracks are 0-1 of the wide frame (`wide` = its width / height), so the strip's cover box is
     widened back out to the box the whole wide frame would fill, the strip's left edge at (1 - its width) * fx of it.
     With the video's object-position at the same fx the picture lands where the wide clip put it, and so do the labels. */
  function strip(box, a, wide, fx) {
    var sw = a / wide, w = box.w / sw;
    return { x: box.x - (1 - sw) * fx * w, y: box.y, w: w, h: box.h, cw: box.cw, ch: box.ch };
  }

  function heroMedia(root) {
    (root || document).querySelectorAll('[data-hero-media]').forEach(function (media) {
      if (media.__heroMedia) return;
      media.__heroMedia = true;
      var hero = media.closest('[data-tmc-hero]') || media;
      var poster = media.querySelector('picture img');
      var button = hero.querySelector('[data-hero-pause]');
      var noMotion = reduce.matches || saveData || slowNetwork();
      var slides = [].map.call(media.querySelectorAll('[data-hero-slide]'), function (el) {
        return {
          id: el.getAttribute('data-hero-slide'), el: el, vids: {}, failed: {}, track: null, time: 0,
          tags: [].map.call(el.querySelectorAll('[data-hero-tag]'), function (t) {
            return { el: t, key: t.getAttribute('data-hero-tag'), x0: +t.getAttribute('data-x'), y0: +t.getAttribute('data-y'),
              hx0: +(t.getAttribute('data-hx') || -1), hy0: +(t.getAttribute('data-hy') || -1),
              phone: t.hasAttribute('data-hero-tag-phone'), bacteria: t.getAttribute('data-hero-kind') === 'bacteria', w: 0, h: 0 };
          })
        };
      });
      if (!slides.length) return;
      var cur = 0, userPaused = false, inView = true, blockers = [], started = false, gaveUp = false;

      /* Which file this width plays: the phone cut at 767 and under, the wide cut above. */
      function cut() { return phone.matches ? 'phone' : 'wide'; }
      function video(s) { return s.vids[cut()] || null; }
      function slot(s, c) { return s.el.querySelector('[data-hero-video="' + (c || cut()) + '"]'); }
      /* What is on screen in a slide: its video, once that has a frame and its layer shows. */
      function onScreen(s) { var v = video(s), w = slot(s); return !!v && v.readyState >= 2 && !!w && w.classList.contains('is-playing'); }

      /* The copy's text lines (not its boxes: the headline's block spans the width), the button, the rail and the
         pause control, relative to the hero. Measured on resize, not per frame. */
      /* A viewport rect as {l, t, r, b} relative to the hero, or null when it has no area. */
      function rel(r, hr) { return r.width && r.height ? { l: r.left - hr.left, t: r.top - hr.top, r: r.right - hr.left, b: r.bottom - hr.top } : null; }
      function measure() {
        var hr = hero.getBoundingClientRect(), out = [];
        function push(r) { var o = rel(r, hr); if (o) out.push(o); }
        function text(el) {
          if (!el) return;
          var range = document.createRange();
          range.selectNodeContents(el);
          [].forEach.call(range.getClientRects(), push);
        }
        hero.querySelectorAll('h1, [data-hero-actions] p, [data-hero-actions] a:not([data-calendly])').forEach(text);
        text(hero.querySelector('h1') && hero.querySelector('h1').previousElementSibling);
        /* NW-97: the Label key is text too; labels keep off it. */
        text(hero.querySelector('[data-hero-key]'));
        hero.querySelectorAll('[data-hero-actions] a[data-calendly], [data-hero-areas]').forEach(function (el) { push(el.getBoundingClientRect()); });
        if (button && !button.hidden) push(button.getBoundingClientRect());
        blockers = out;
        slides.forEach(function (s) { s.tags.forEach(function (t) { t.w = 0; }); });
      }

      /* The media's intrinsic frame and where object-fit: cover and object-position put it in the hero. */
      function frame(s, usePoster) {
        var v = usePoster ? null : video(s), ref = v && v.videoWidth ? v : poster;
        var iw = v && v.videoWidth ? v.videoWidth : (poster && poster.naturalWidth) || 1920;
        var ih = v && v.videoHeight ? v.videoHeight : (poster && poster.naturalHeight) || 1084;
        var pos = ref ? getComputedStyle(ref).objectPosition.split(' ') : ['50%', '50%'];
        var fx = parseFloat(pos[0]) / 100, box = cover(iw, ih, media.clientWidth, media.clientHeight, fx, parseFloat(pos[1] || pos[0]) / 100);
        if (!v || !(ih > iw)) return box;
        /* A portrait video is a phone cut of the wide clip: widen its box back out to the wide frame's. */
        var fw = s.track ? s.track.w : (poster && poster.naturalWidth) || 1920;
        var fh = s.track ? s.track.h : (poster && poster.naturalHeight) || 1084;
        return strip(box, iw / ih, fw / fh, fx);
      }
      function point(s, t, poster0) {
        var pts = s.track && s.track.labels[t.key];
        if (poster0 || !pts) return [t.x0, t.y0];
        /* NW-98: a track may end before its clip does (the walker has left the frame). Past its last point the label
           has no person to follow: null, and place() fades it out where it stood rather than pinning it to the edge. */
        var f = Math.max(0, s.time * s.track.fps);
        if (f > pts.length - 1) return null;
        var i0 = Math.floor(f), i1 = Math.min(pts.length - 1, i0 + 1), a = f - i0;
        /* A person who walks in after frame 0 has no point until they do: null, and the label waits, hidden. */
        if (!pts[i0] || !pts[i1]) return null;
        return [pts[i0][0] + (pts[i1][0] - pts[i0][0]) * a, pts[i0][1] + (pts[i1][1] - pts[i0][1]) * a];
      }
      /* The top of the label's person's head (the track's `heads`, or data-hx/hy at frame 0), for the phone's floating
         labels; null where there is none, and the label's own anchor stands in. */
      function head(s, t, poster0) {
        var pts = s.track && s.track.heads && s.track.heads[t.key];
        if (poster0) return t.hx0 >= 0 ? [t.hx0, t.hy0] : null;
        if (!pts) return null;
        var f = Math.max(0, s.time * s.track.fps);
        if (f > pts.length - 1) return null;
        var i0 = Math.floor(f), i1 = Math.min(pts.length - 1, i0 + 1), a = f - i0;
        if (!pts[i0] || !pts[i1]) return null;
        return [pts[i0][0] + (pts[i1][0] - pts[i0][0]) * a, pts[i0][1] + (pts[i1][1] - pts[i0][1]) * a];
      }
      function hits(r, pad) {
        return function (o) { return r.l < o.r + pad && r.r > o.l - pad && r.t < o.b + pad && r.b > o.t - pad; };
      }
      function place(s) {
        /* Labels follow what is on screen: a playing clip's frame (once its tracks are in: without them they would pin to
           frame 0 while people walk away), or, while no clip layer shows, clip 01's poster at frame 0. Another clip's
           slide with nothing loaded yet shows the poster underneath, so its labels wait. */
        var shown0 = !onScreen(s), live = s === slides[cur] && (shown0 ? s === slides[0] : !!s.track);
        var box = frame(s, shown0), hr = hero.getBoundingClientRect(), shown = [], all = blockers.slice();
        var nav = document.querySelector('[data-tmc-nav] nav');
        var nr = nav && rel(nav.getBoundingClientRect(), hr);
        if (phone.matches) { float(s, box, live, shown0, nr); return; }
        if (nr) all.push(nr);
        s.tags.forEach(function (t) {
          if (!live || (phone.matches && !t.phone)) { t.el.classList.remove('is-on'); return; }
          if (!t.w) { t.w = t.el.offsetWidth; t.h = t.el.offsetHeight; }
          var p = point(s, t, shown0);
          if (!p) { t.el.classList.remove('is-on'); return; }
          var x = box.x + p[0] * box.w, y = box.y + p[1] * box.h;
          /* The anchor is the white square's centre: 9px in, half the label's height down. */
          var lx = x - 9, ly = y - t.h / 2, r = { l: lx, t: ly, r: lx + t.w, b: ly + t.h };
          var on = r.l >= EDGE && r.t >= EDGE && r.r <= box.cw - EDGE && r.b <= box.ch - EDGE &&
            !all.some(hits(r, CLEAR)) && !shown.some(hits(r, GAP));
          if (on) shown.push(r);
          /* left/top, not a transform: tmc-home.css sets transform: none on everything in a section under reduced motion. */
          t.el.style.left = lx.toFixed(1) + 'px';
          t.el.style.top = ly.toFixed(1) + 'px';
          t.el.classList.toggle('is-on', on);
        });
      }
      function placeAll() { slides.forEach(place); }

      /* Phones (26 Sep 2026, Jackson): a label floats above its person's head, as a chip like the Contact page's, with a
         thin line down to them. It keeps between the nav and the copy: a person under the headline gets a label at the
         foot of that band and a longer line. A label that would meet one already placed moves up past it; one that
         still cannot fit, or whose person is off screen, fades. At most 4 Symptom and 1 Bacteria labels show at once,
         the earlier in block order first. Each drifts a few pixels as it plays, like the Contact page's chips; not under
         reduced motion. */
      var CAP = { symptom: 4, bacteria: 1 };
      var LIFT = 10;  /* px between a label's foot and its person's head */
      var SVG = 'http://www.w3.org/2000/svg';
      function lineFor(s, t) {
        if (t.line) return t.line;
        if (!s.svg) {
          s.svg = document.createElementNS(SVG, 'svg');
          s.svg.setAttribute('class', 'tmc-hero__lines');
          s.svg.setAttribute('aria-hidden', 'true');
          var host = s.el.querySelector('.tmc-hero__tags') || s.el;
          host.insertBefore(s.svg, host.firstChild);
        }
        var g = document.createElementNS(SVG, 'g'), l = document.createElementNS(SVG, 'line'), c = document.createElementNS(SVG, 'circle');
        l.setAttribute('stroke', 'rgba(255,255,255,.5)');
        l.setAttribute('stroke-width', '1');
        c.setAttribute('r', '2');
        c.setAttribute('fill', 'rgba(255,255,255,.85)');
        g.appendChild(l);
        g.appendChild(c);
        s.svg.appendChild(g);
        return (t.line = { g: g, l: l, c: c });
      }
      function float(s, box, live, shown0, nr) {
        var top = Math.max(EDGE, nr ? nr.b + CLEAR : EDGE), foot = box.ch - EDGE;
        blockers.forEach(function (o) { if (o.t > box.ch * 0.3) foot = Math.min(foot, o.t - CLEAR); });
        var shown = [], count = { symptom: 0, bacteria: 0 }, now = (window.performance ? performance.now() : Date.now()) / 1000;
        s.tags.forEach(function (t, i) {
          var kind = t.bacteria ? 'bacteria' : 'symptom', on = false, ln = null;
          var p = live && t.phone && count[kind] < CAP[kind] ? point(s, t, shown0) : null;
          var hd = p && (head(s, t, shown0) || p);
          if (hd) {
            if (!t.w) { t.w = t.el.offsetWidth; t.h = t.el.offsetHeight; }
            var hx = box.x + hd[0] * box.w, hy = box.y + hd[1] * box.h;
            if (hx >= 0 && hx <= box.cw && hy >= 0 && hy <= box.ch) {
              var lx = Math.min(Math.max(hx - t.w / 2, EDGE), box.cw - EDGE - t.w);
              var ly = Math.max(top, Math.min(hy - LIFT - t.h, foot - t.h));
              var r = { l: lx, t: ly, r: lx + t.w, b: ly + t.h }, hit, n = 0;
              while ((hit = shown.filter(hits(r, GAP))[0]) && n++ < shown.length) {
                ly = hit.t - GAP - t.h;
                r = { l: lx, t: ly, r: lx + t.w, b: ly + t.h };
              }
              on = ly >= top && ly + t.h <= foot && !shown.some(hits(r, GAP));
              if (on) {
                shown.push(r);
                count[kind]++;
                var still = reduce.matches, dx = still ? 0 : Math.sin(now * 0.6 + i * 1.9) * 2, dy = still ? 0 : Math.sin(now * 0.8 + i * 2.7) * 3;
                t.el.style.left = (lx + dx).toFixed(1) + 'px';
                t.el.style.top = (ly + dy).toFixed(1) + 'px';
                /* The line leaves the chip's foot above the head and ends on it; none if the chip sits on the head. */
                if (ly + t.h + 4 < hy) {
                  ln = lineFor(s, t);
                  var x1 = Math.min(Math.max(hx, lx + 14), lx + t.w - 14) + dx;
                  ln.l.setAttribute('x1', x1.toFixed(1));
                  ln.l.setAttribute('y1', (ly + t.h + dy).toFixed(1));
                  ln.l.setAttribute('x2', hx.toFixed(1));
                  ln.l.setAttribute('y2', (hy - 2).toFixed(1));
                  ln.c.setAttribute('cx', hx.toFixed(1));
                  ln.c.setAttribute('cy', (hy - 2).toFixed(1));
                }
              }
            }
          }
          t.el.classList.toggle('is-on', on);
          if (t.line) t.line.g.classList.toggle('is-on', !!ln);
        });
      }

      function showButton(on) {
        if (!button) return;
        var was = !button.hidden;
        if (on && button.hidden) { button.hidden = false; void button.offsetWidth; }
        button.classList.toggle('is-on', on);
        if (!on) button.hidden = true;
        /* The pause control is a blocker only while it shows, so the labels re-measure when it comes and goes. */
        if (was !== on) { measure(); placeAll(); }
      }
      function playable(s) { return !!slot(s) && !s.failed[cut()]; }
      function after(i) {
        for (var n = 1; n <= slides.length; n++) { var j = (i + n) % slides.length; if (playable(slides[j])) return j; }
        return -1;
      }
      function activate(i) {
        cur = i;
        slides.forEach(function (s, n) { s.el.classList.toggle('is-active', n === i); });
        placeAll();
      }
      function running() { return !userPaused && inView && !document.hidden; }

      function fail(s, k) {
        var v = s.vids[k];
        if (v) { clearTimeout(v.__timer); v.remove(); }
        delete s.vids[k];
        s.failed[k] = true;
        var w = slot(s, k);
        if (w) w.classList.remove('is-playing');
        if (s === slides[cur] && k === cut()) {
          var j = after(cur);
          if (j >= 0 && j !== cur) { activate(j); load(slides[j]); sync(); }
          else { showButton(false); slides[0].time = 0; activate(0); }
        }
      }

      /* Each presented frame moves that clip's labels; the current clip also decides when to hand over. */
      function onFrame(s, v, t) {
        if (s.vids[cut()] !== v) return;
        s.time = t;
        place(s);
        if (s !== slides[cur] || !v.duration) return;
        if (t < v.duration - HANDOFF) { s.handed = false; return; }
        if (!s.handed) handoff();
      }
      function watch(s, v) {
        if (v.requestVideoFrameCallback) {
          var cb = function (now, meta) { if (!v.isConnected) return; onFrame(s, v, meta.mediaTime); v.requestVideoFrameCallback(cb); };
          v.requestVideoFrameCallback(cb);
          return;
        }
        (function tick() {
          if (!v.isConnected) return;
          if (!v.paused) onFrame(s, v, v.currentTime);
          window.requestAnimationFrame(tick);
        })();
      }

      /* Back to the poster for good: every clip layer fades out (the wrappers' 400ms) and is then removed, clip 01's
         slide returns with its labels at frame 0, the pause control goes, and nothing loads or rotates again. */
      function giveUp() {
        if (gaveUp) return;
        gaveUp = true;
        showButton(false);
        slides.forEach(function (o) {
          Object.keys(o.vids).forEach(function (k) {
            var v = o.vids[k], w = slot(o, k);
            clearTimeout(v.__timer);
            v.pause();
            if (w) w.classList.remove('is-playing');
            setTimeout(function () { v.removeAttribute('src'); v.querySelectorAll('source').forEach(function (x) { x.remove(); }); v.load(); v.remove(); }, 450);
          });
          o.vids = {};
          o.failed = { wide: true, phone: true };
        });
        slides[0].time = 0;
        activate(0);
      }
      /* Counts the stalls of a clip while it is the one on screen and meant to be playing. A seek, a pause or the
         visitor's own pause is not a stall, and neither is a blip: a stall counts once it has lasted BLIP_MS, so a few
         momentary hiccups over minutes of rotation never take the video away. */
      function guard(s, v) {
        var blip = null, long = null, count = 0;
        function clear() { clearTimeout(blip); clearTimeout(long); blip = long = null; }
        function stalled() { return !gaveUp && !v.paused && v.readyState < 3 && s === slides[cur]; }
        v.addEventListener('waiting', function () {
          if (gaveUp || v.seeking || v.paused || s !== slides[cur] || !running() || !slot(s).classList.contains('is-playing')) return;
          clear();
          blip = setTimeout(function () { if (stalled() && ++count >= STALLS) giveUp(); }, BLIP_MS);
          long = setTimeout(function () { if (stalled()) giveUp(); }, STALL_MS);
        });
        v.addEventListener('playing', clear);
        v.addEventListener('pause', clear);
      }
      /* Started once per file and cleared by canplay: a handover retried on each loop of the current clip must not
         restart it, or a clip that never loads would never be dropped. */
      function deadline(s, k, v) {
        if (v.__timer) return;
        v.__timer = setTimeout(function () { if (v.readyState < 3) fail(s, k); }, TIMEOUT);
      }
      function load(s) {
        var k = cut();
        if (gaveUp || !s || s.vids[k] || s.failed[k]) return;
        var w = slot(s, k), tpl = w && w.querySelector('template');
        var src = tpl && tpl.content.querySelector('video');
        if (!src) return fail(s, k);
        /* Stripped inside the inert template, so neither image is ever fetched. The rotation plays each clip itself,
           so autoplay goes; loop stays, so a clip whose successor is not ready yet repeats instead of freezing. */
        src.removeAttribute('poster');
        src.removeAttribute('autoplay');
        src.querySelectorAll('img').forEach(function (i) { i.remove(); });
        var v = document.importNode(src, true);
        v.muted = true;
        v.playsInline = true;
        v.autoplay = false;
        v.preload = s === slides[cur] ? 'metadata' : 'auto';
        if (s === slides[0] && poster) v.poster = poster.currentSrc || poster.src;
        s.vids[k] = v;
        /* Only a clip that has to be on screen is timed out. The next clip loads quietly: iOS ignores preload on a video
           that is not playing, so it would never reach canplay in time, and dropping it would stop the rotation. */
        if (s === slides[cur]) deadline(s, k, v);
        v.addEventListener('canplay', function () { clearTimeout(v.__timer); v.__timer = true; if (s === slides[cur]) sync(); }, { once: true });
        /* A file swapped in at another width picks up the frame the other one was showing. Once the rotation has played,
           a clip's layer shows as soon as it has a frame, so a paused swap never uncovers clip 01's poster. */
        v.addEventListener('loadedmetadata', function () { if (s.time > 0) v.currentTime = s.time; }, { once: true });
        v.addEventListener('loadeddata', function () { if (media.__played && s.vids[k] === v) { w.classList.add('is-playing'); place(s); } }, { once: true });
        v.addEventListener('playing', function () {
          if (s.vids[k] !== v) return;
          media.__played = true;
          w.classList.add('is-playing');
          place(s);
          if (s === slides[cur]) { showButton(true); load(slides[after(cur)]); }
        });
        /* Paused or scrubbed, the labels still sit on the frame being shown. */
        v.addEventListener('pause', function () { s.time = v.currentTime; place(s); });
        v.addEventListener('seeked', function () { s.time = v.currentTime; place(s); });
        v.addEventListener('error', function () { fail(s, k); });
        /* With <source> children the error lands on each source; the video has failed once none is left. */
        v.querySelectorAll('source').forEach(function (source) {
          source.addEventListener('error', function () {
            setTimeout(function () { if (v.networkState === HTMLMediaElement.NETWORK_NO_SOURCE) fail(s, k); }, 0);
          });
        });
        w.appendChild(v);
        watch(s, v);
        guard(s, v);
      }

      function handoff() {
        if (gaveUp) return;
        var s = slides[cur], j = after(cur);
        if (j < 0 || j === cur) return;
        var next = slides[j], nv = video(next);
        if (!nv) { load(next); return; }
        s.handed = true;
        /* play() also starts the download where preload was ignored. The old clip loops meanwhile; a clip that cannot
           play within TIMEOUT leaves the rotation. */
        if (nv.readyState < 3) deadline(next, cut(), nv);
        nv.currentTime = 0;
        next.time = 0;
        var p = nv.play();
        var go = function () {
          if (slides[cur] !== s) return;
          activate(j);
          load(slides[after(j)]);
          var old = video(s);
          setTimeout(function () { if (old && slides[cur] !== s) old.pause(); }, 1000);
        };
        if (p && p.then) p.then(go, function () { s.handed = false; }); else go();
      }

      function sync() {
        if (gaveUp) return;
        var s = slides[cur], v = video(s);
        if (!v) return;
        if (!running()) { slides.forEach(function (o) { var ov = video(o); if (ov) ov.pause(); }); return; }
        var p = v.play();
        /* A pause() before the first frame rejects this play() with AbortError; that is ours, not a failure. Anything
           else before it has ever played (autoplay refused, e.g. a phone in low-power mode) leaves the poster alone. */
        if (p && p.catch) p.catch(function (e) {
          if (e && e.name !== 'AbortError' && !slot(s).classList.contains('is-playing')) fail(s, cut());
        });
      }
      function choose() {
        if (gaveUp) { placeAll(); return; }
        slides.forEach(function (o) { Object.keys(o.vids).forEach(function (k) { o.vids[k].pause(); }); });
        var i = playable(slides[cur]) ? cur : after(cur);
        if (i < 0) { showButton(false); slides[0].time = 0; activate(0); return; }
        activate(i);
        load(slides[i]);
        var v = video(slides[i]);
        showButton(!!v && slot(slides[i]).classList.contains('is-playing'));
        sync();
      }

      measure();
      activate(0);
      window.addEventListener('resize', function () { measure(); placeAll(); }, { passive: true });
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { measure(); placeAll(); });
      window.addEventListener('scroll', function () { place(slides[cur]); }, { passive: true });
      if (noMotion || !media.querySelector('[data-hero-video]')) {
        window.addEventListener('load', function () { measure(); placeAll(); }, { once: true });
        if (phone.addEventListener) phone.addEventListener('change', placeAll); else phone.addListener(placeAll);
        return;
      }

      if (button) button.addEventListener('click', function () {
        userPaused = !userPaused;
        button.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
        sync();
      });
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          var en = entries[entries.length - 1];
          inView = en.isIntersecting && en.intersectionRatio >= 0.1;
          sync();
        }, { threshold: [0, 0.1] }).observe(hero);
      }
      document.addEventListener('visibilitychange', sync);
      function go() {
        if (started) return;
        started = true;
        measure();
        var url = media.getAttribute('data-hero-tracks');
        if (url && window.fetch) {
          fetch(url).then(function (r) { return r.json(); }).then(function (data) {
            slides.forEach(function (s) { s.track = (data.clips || {})[s.id] || null; });
            placeAll();
          }).catch(function (e) {
            /* The clips still rotate; only their labels stay hidden, since they cannot be tracked. */
            if (window.console) console.warn('tmc-hero: label tracks unavailable', e);
          });
        }
        choose();
        if (phone.addEventListener) phone.addEventListener('change', choose); else phone.addListener(choose);
      }
      if (document.readyState === 'complete') go();
      else window.addEventListener('load', go, { once: true });
    });
  }

  /* Quiz phone loop (NW-23, board motion-38a-phone-loop-46). The 12s timeline is CSS in sections/tmc-how-it-works.liquid;
     this only says when it runs. .is-loop arms it, paused on its first frame. .is-in lifts the phone once, the first time
     30% of the stage is on screen, and the pause button shows with it. .is-playing runs the clock while 30% is on screen, the tab is visible and the pause
     button is not pressed; a pause holds until it is pressed again. Reduced motion: never armed, no button, and the
     phone rests on the section's static question screen. */
  function phoneLoop(root) {
    (root || document).querySelectorAll('[data-tmc-qphone]').forEach(function (qp) {
      if (qp.__loop || reduce.matches || !('IntersectionObserver' in window)) return;
      qp.__loop = true;
      var stage = qp.parentNode;
      var button = stage.querySelector('[data-qp-pause]');
      var userPaused = false, inView = false;

      function sync() {
        qp.classList.toggle('is-playing', inView && !userPaused && !document.hidden);
      }
      qp.classList.add('is-loop');
      if (button) {
        button.addEventListener('click', function () {
          userPaused = !userPaused;
          button.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
          sync();
        });
      }
      /* The 90% guard is the reveal observer's: a stage too tall to ever show 30% still plays once it fills the screen. */
      new IntersectionObserver(function (entries) {
        var en = entries[entries.length - 1];
        inView = en.isIntersecting && (en.intersectionRatio >= 0.29 ||
          !!(en.rootBounds && en.intersectionRect.height >= en.rootBounds.height * 0.9));
        if (inView && !qp.classList.contains('is-in')) {
          qp.classList.add('is-in');
          if (button) button.hidden = reduce.matches;
        }
        sync();
      }, { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.6, 0.8, 1] }).observe(stage);
      document.addEventListener('visibilitychange', sync);
      /* Turned on mid-visit, reduced motion drops the CSS loop, so the button would pause nothing. */
      function onReduce() { if (button) button.hidden = reduce.matches || !qp.classList.contains('is-in'); }
      if (reduce.addEventListener) reduce.addEventListener('change', onReduce); else reduce.addListener(onReduce);
    });
  }

  /* Hero area strip (NW-61, #329, board "Home · Hero · motion · board"). Every width: an area's button scrolls to its card
     in the conditions grid (#areas, NW-58; the card is the grid item with the same data-cond), with --ease
     (cubic-bezier(.22, 1, .36, 1), an ease-out quint) over SCROLL_MS, or a jump under reduced motion. At 390 the rail
     rolls left at data-hero-roll-speed px/s (24), linear, over its second copy, wrapping by one set's width so the loop
     has no seam. It is driven by scrollLeft, so a finger can still swipe it. It holds on touch or pointer down and on
     focus, and resumes RESUME_MS after release; it stops while the hero is off screen or the tab is hidden. Reduced
     motion: no roll and no second copy, a still row to swipe. */
  var SCROLL_MS = 700;
  var RESUME_MS = 2000;
  function scrollToY(y) {
    if (reduce.matches) { window.scrollTo(0, y); return; }
    var y0 = window.pageYOffset, t0 = 0;
    function step(t) {
      if (!t0) t0 = t;
      var k = Math.min(1, (t - t0) / SCROLL_MS);
      window.scrollTo(0, y0 + (y - y0) * (1 - Math.pow(1 - k, 5)));
      if (k < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }
  function heroStrip(root) {
    (root || document).querySelectorAll('[data-hero-areas]').forEach(function (rail) {
      if (rail.__strip) return;
      rail.__strip = true;
      var hero = rail.closest('[data-tmc-hero]') || rail;
      var speed = parseFloat(rail.getAttribute('data-hero-roll-speed')) || 24;
      var pos = 0, last = 0, raf = 0, held = false, inView = true, timer = 0;

      rail.addEventListener('click', function (e) {
        var go = e.target.closest('[data-hero-area-go]');
        var grid = go && document.getElementById('areas');
        if (!grid) return;
        var card = grid.querySelector('[data-cond="' + go.getAttribute('data-hero-area-go') + '"]') || grid;
        var margin = parseFloat(getComputedStyle(grid).scrollMarginTop) || 0;
        scrollToY(Math.max(0, card.getBoundingClientRect().top + window.pageYOffset - margin));
        /* Keyboard users land where they looked: focus moves to a control in the card, with no second jump. */
        var next = [].filter.call(card.querySelectorAll('button, a[href], [tabindex="-1"]'), function (el) { return el.offsetParent; })[0];
        if (next) next.focus({ preventScroll: true });
      });

      function span() {
        var a = rail.querySelector('[data-hero-area]'), b = rail.querySelector('[data-hero-area-dup]');
        return a && b && b.offsetParent ? b.offsetLeft - a.offsetLeft : 0;
      }
      function rolling() { return phone.matches && !reduce.matches; }
      function tick(t) {
        raf = 0;
        if (!rolling() || held || !inView || document.hidden) { last = 0; return; }
        var w = span();
        if (last && w > 0) {
          pos += (t - last) / 1000 * speed;
          while (pos >= w) pos -= w;
          rail.scrollLeft = pos;
        }
        last = t;
        raf = requestAnimationFrame(tick);
      }
      function sync() {
        var on = rolling();
        if (rail.classList.contains('is-rolling') !== on) {
          rail.classList.toggle('is-rolling', on);
          rail.scrollLeft = 0;
          pos = 0;
        }
        if (on && !raf) { last = 0; raf = requestAnimationFrame(tick); }
      }
      function hold() { held = true; clearTimeout(timer); }
      function release() {
        clearTimeout(timer);
        timer = setTimeout(function () {
          var w = span();
          pos = rail.scrollLeft;
          if (w > 0 && pos >= w) { pos -= w; rail.scrollLeft = pos; }
          held = false;
          sync();
        }, RESUME_MS);
      }
      rail.addEventListener('pointerdown', hold);
      rail.addEventListener('touchstart', hold, { passive: true });
      rail.addEventListener('pointerup', release);
      rail.addEventListener('pointercancel', release);
      rail.addEventListener('touchend', release);
      rail.addEventListener('focusin', hold);
      rail.addEventListener('focusout', function (e) { if (!rail.contains(e.relatedTarget)) release(); });
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          inView = entries[entries.length - 1].isIntersecting;
          sync();
        }).observe(hero);
      }
      document.addEventListener('visibilitychange', sync);
      [phone, reduce].forEach(function (mq) { if (mq.addEventListener) mq.addEventListener('change', sync); else mq.addListener(sync); });
      sync();
    });
  }

  function init() { reveal(); parallax(); heroMedia(); heroStrip(); phoneLoop(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', function (e) { reveal(e.target); parallax(); heroMedia(e.target); heroStrip(e.target); phoneLoop(e.target); });
})();
